import baseApi from '../../base.api'
import { openNotificationWithIcon } from '../../../../utils/notification.helper'

/**
 * Cache Key Template approach for updating multiple cache entries dynamically
 * Based on industry practices from CDN providers like Cloudflare and Qwilt
 * 
 * This function finds and updates all existing cache entries that match the given criteria,
 * rather than trying to enumerate all possible parameter combinations.
 * 
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} endpointName - The RTK Query endpoint name (e.g., 'fetchQuestionAnswers')
 * @param {Function} filterFn - Function that receives cache params and returns true if entry should be updated
 * @param {Function} updateFunction - Function to apply to matching cache entries
 * @returns {Array} Array of patch results for error handling
 */
export const updateMatchingCacheEntries = (dispatch, endpointName, filterFn, updateFunction) => {
    const patchResults = []
    
    // Get the current state of the RTK Query cache
    const state = dispatch((_, getState) => getState())
    const apiState = state.api
    
    // Find all existing cache entries for this endpoint
    const endpointQueries = Object.keys(apiState.queries)
        .filter(key => key.startsWith(`${endpointName}(`))
        .map(key => {
            try {
                // Extract the cache key parameters from the RTK Query cache key
                const match = key.match(new RegExp(`${endpointName}\\((.+)\\)`))
                if (match) {
                    const params = JSON.parse(match[1])
                    return { cacheKey: key, params }
                }
            } catch (e) {
                // Skip malformed cache keys
            }
            return null
        })
        .filter(entry => entry !== null)
        .filter(entry => filterFn(entry.params)) // Apply custom filter

    // Update all matching cache entries
    endpointQueries.forEach(({ params }) => {
        const patchResult = dispatch(
            baseApi.util.updateQueryData(endpointName, params, (draft) => updateFunction(draft, params)),
        )
        patchResults.push(patchResult)
    })

    return patchResults
}

export const handleDeleteOnQueryStarted =
    (entityKey, idKey = 'id') =>
    async ({ [idKey]: id }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
            baseApi.util.updateQueryData(entityKey, undefined, draft => {
                const index = draft.findIndex(item => item[idKey] === id)
                if (index !== -1) draft.splice(index, 1)
            }),
        )

        try {
            await queryFulfilled
        } catch (error) {
            patchResult.undo()
            openNotificationWithIcon(
                'error',
                'Delete failed',
                'Conceptual label delete failed. Please, try again later.',
            )
            console.log(error)
        }
    }

export const handleUpdateOnQueryStarted =
    (entityKey, normalizer = data => data, idKey = 'id') =>
    async ({ [idKey]: id }, { dispatch, queryFulfilled }) => {
        try {
            const { data } = await queryFulfilled
            dispatch(
                baseApi.util.updateQueryData(entityKey, undefined, draft => {
                    const index = draft.findIndex(item => item[idKey] === id)
                    if (index !== -1) {
                        draft[index] = normalizer(data)
                    }
                }),
            )
        } catch (error) {
            console.log(error)
        }
    }

export const handleCreateOnQueryStarted =
    (entityKey, normalizer = data => data) =>
    async (newItem, { dispatch, queryFulfilled }) => {
        try {
            const { data } = await queryFulfilled
            dispatch(
                baseApi.util.updateQueryData(entityKey, undefined, draft => {
                    draft.push(normalizer(data))
                }),
            )
        } catch (error) {
            console.log(error)
        }
    }
